import * as Sentry from '@sentry/react';
import i18n from '@portal/i18n/config';
import { sendError } from './crashlytics';

type I18nOptions = Record<string, string | number | boolean>;

type I18nTSecondParam = Exclude<
  Parameters<typeof i18n.t>[1],
  string | undefined
>;

function callI18nT(
  key: string,
  options?: I18nOptions & { returnObjects?: boolean }
) {
  if (options) {
    return i18n.t(key, options as I18nTSecondParam);
  }
  return i18n.t(key);
}

export function translate<T extends I18nOptions = I18nOptions>(
  key: string,
  options?: T & { returnObjects?: boolean }
) {
  try {
    const shouldReturnObjects =
      options?.returnObjects || key.startsWith('ENUMERATORS.');

    const translationOptions = shouldReturnObjects
      ? { ...options, returnObjects: true }
      : options;

    let translation = callI18nT(
      key || 'APPLICATION.ERRORS.INVALID_KEY',
      translationOptions
    );

    if (typeof translation === 'object' && translation !== null) {
      return translation;
    }

    const isMissing =
      translation === key ||
      (typeof translation === 'string' && translation.includes('[missing '));

    if (isMissing) {
      if (
        key.split('.')[0] === 'TRANSACTION_TYPES' ||
        key.startsWith('COMPONENTS.CONFIRMATION_DETAILS.TRANSACTION_TYPES')
      ) {
        return i18n.t(
          'COMPONENTS.CONFIRMATION_DETAILS.TRANSACTION_TYPES.UNKNOWN'
        );
      }

      if (import.meta.env.NODE_ENV === 'production') {
        sendError(new Error(`Translation not set: ${key}`));
      }

      Sentry.captureEvent({
        message: `[i18n] missing translation: ${key}`,
        level: 'warning',
        tags: {
          'error.domain': 'i18n',
          'i18n.missing_key': key,
          'i18n.locale': i18n.language ?? 'pt-BR',
        },
        extra: { key, locale: i18n.language ?? 'pt-BR' },
        fingerprint: ['i18n-missing', key, String(i18n.language ?? 'pt-BR')],
        transaction: `i18n.missing.${key}`,
      });

      return i18n.t('APPLICATION.ERRORS.TRANSLATION_NOT_SET');
    }

    if (options && typeof translation === 'string') {
      Object.keys(options).forEach((field) => {
        const value = options[field];
        translation = (translation as string).replace(
          `{{${field}}}`,
          String(value || '')
        );
      });
    }

    return translation;
  } catch (err) {
    return i18n.t('APPLICATION.ERRORS.GENERIC_ERROR');
  }
}

export function translateOnlyIfAvailable<T extends I18nOptions = I18nOptions>(
  key: string,
  options?: T
) {
  const lastKey = key.split('.').pop();
  const translated = callI18nT(
    key || 'APPLICATION.ERRORS.INVALID_KEY',
    options
  );

  if (lastKey === translated) {
    return false;
  }

  return translated;
}
